// Shared normalisation + export helpers for the IPD patient register views
// (Allocated / Admitted / Discharged / Death Register). Extracted from the
// original single-page AdmittedDischargedPatientList (Task C18) so all four
// views normalise admissions and export identically.
import { departmentLabel } from "../../utils/departmentLabel";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs || pdfFonts;

export function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

export function fmt(dt) {
  if (!dt) return "-";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return dt;
  }
}

// An ISO timestamp rendered for a datetime-local input ("YYYY-MM-DDTHH:mm"),
// in LOCAL time — toISOString() would shift the value by the UTC offset and
// silently show the wrong time of death.
export function toDatetimeLocal(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Maps a raw ipd_admission/ row (main/ipd/manage_admission.py list
// serializer) to display fields — same join-a-nested-object approach as
// useBedCensusData.js's normalizeCensusAdmissions().
export function normalizeAdmissionRow(row) {
  const p = row?.patient || {};
  const name =
    p.name || [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—";
  return {
    admissionId: row?.admission_id ?? null,
    patientName: name,
    uhid: p.uhid || "",
    age: calcAge(p.dob),
    gender: p.gender || "",
    // The ward IS the bed type. This used to read ward_name, which carried a
    // department name from main_ipd_ward. `location` is a floor/room string
    // ("Floor 1 - Room 103") and is the last resort, not a ward.
    ward: row?.bed_type_name || row?.location || "—",
    bedTypeName: row?.bed_type_name || "—",
    // Department follows the Consultant, not the bed. Reads "Department unassigned"
    // when the Consultant's department matched no medical department.
    department: departmentLabel(row?.department_name, row?.department_id),
    admittingDoctor: row?.doctors?.admitting?.name || "—",
    admittedAt: row?.admitted_at || null,
    status: row?.status || "—",
  };
}

function csvEscape(value) {
  const str = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// A `columns` entry is {header, get}. Both exporters take the same list, so a
// view's CSV and PDF can never drift from each other or from its table.
export function exportCsv(columns, rows, filenameBase) {
  const lines = [
    columns.map((col) => csvEscape(col.header)).join(","),
    ...rows.map((r) => columns.map((col) => csvEscape(col.get(r))).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function exportPdf(columns, rows, filenameBase, title) {
  const body = [
    columns.map((col) => ({ text: col.header, bold: true, fontSize: 9 })),
    ...rows.map((r) =>
      columns.map((col) => ({ text: String(col.get(r)), fontSize: 8 }))
    ),
  ];
  pdfMake
    .createPdf({
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [30, 30, 30, 30],
      content: [
        { text: title, fontSize: 14, bold: true, margin: [0, 0, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: Array(columns.length).fill("auto"),
            body,
          },
          layout: "lightHorizontalLines",
        },
      ],
      defaultStyle: { fontSize: 8 },
    })
    .download(`${filenameBase}.pdf`);
}
